import Cookies from "js-cookie";
import { useFetchData, usePostData, useDeleteData } from "utils/ReactQueryHooks";
import { isData } from "utils/HookResponse";
import { ApiResponseData } from "types/common";
import { STALE } from "constants/stale";

export const useLicenses = () =>
  useFetchData({
    key: "licenses",
    url: "licenses",
    data: {
      aId: Cookies.get("aId"),
    },
    options: {
      select: (data: ApiResponseData) => isData(data),
    },
    post: true,
  });

export const useLicensePools = (serials: (string | null | undefined)[]) =>
  useFetchData({
    key: "licensePools",
    url: "licenses/pools",
    post: true,
    data: serials
      ?.filter((i): i is string => typeof i === "string" && i !== null)
      .map((i) => i.toLowerCase()),
    options: {
      enabled: false,
      select: (data: ApiResponseData) => isData(data),
    },
  });

export const useLicensePool = (serial: string, linkedPool?: boolean) =>
  useFetchData({
    key: linkedPool ? `licensePool-linked-${serial}` : `licensePool-${serial}`,
    url: "licenses/pool",
    post: true,
    data: linkedPool ? { poolId: serial?.toLowerCase() } : [serial?.toLowerCase()],
    options: {
      enabled: false,
      select: (data: ApiResponseData) => isData(data),
    },
  });

// Separate hook for paginated outbound allocations
// TODO: Change default size to 5 before deploying to production
export const useOutboundAllocations = (
  poolId: string,
  paginationParams?: { page?: number; size?: number; date_gte?: string; date_lte?: string }
) =>
  useFetchData({
    key: `outboundAllocations-${poolId}-page-${paginationParams?.page || 1}-${paginationParams?.date_gte || ''}-${paginationParams?.date_lte || ''}`,
    url: "licenses/outbound-allocations",
    post: true,
    data: {
      poolId: poolId?.toLowerCase(),
      page: paginationParams?.page,
      size: paginationParams?.size,
      date_gte: paginationParams?.date_gte,
      date_lte: paginationParams?.date_lte,
    },
    options: {
      enabled: !!poolId,
      select: (data: ApiResponseData) => isData(data),
    },
  });

export const useLicenseNotificationUpdate = () =>
  usePostData({ url: "licenses/update-notification" });

export const useLicenseAliasUpdate = () =>
  usePostData({ url: "licenses/update-alias" });

// Fetch account admins (only Admin profile users with completed onboarding)
// Note: License Managers for pool assignment come from /licenses endpoint (SF data)
export const useAccountAdmins = () =>
  useFetchData({
    key: "accountAdmins",
    url: "licenses/admins",
    data: {
      aId: Cookies.get("aId"),
    },
    options: {
      select: (data: ApiResponseData) => isData(data),
      staleTime: STALE.MEDIUM, // Prevent unnecessary refetches during CM operations
    },
    post: true,
  });

// User-centric query for License Managers - fetches pools assigned to a specific LM
// Now uses email as the identifier (externalId in PAM)
export const useMyAssignedPools = (userEmail: string | null, enabled = false) =>
  useFetchData({
    key: `myAssignedPools-${userEmail}`,
    url: "licenses/my-assigned-pools",
    data: {
      userEmail,
    },
    options: {
      enabled: enabled && !!userEmail,
      select: (data: ApiResponseData) => isData(data),
    },
    post: true,
  });

export const useAssignLicenseManagers = () =>
  usePostData({ url: "licenses/assign-managers" });

export const useRemoveLicenseManager = () =>
  usePostData({ url: "licenses/remove-manager" });

// LM-specific hook: Fetch pre-filtered hierarchy containing only assigned pools
// Uses email as the identifier (externalId in PAM)
// Note: aId is still needed for SF hierarchy fetch (federation ID)
export const useMyHierarchy = (userEmail: string | null, enabled = false) =>
  useFetchData({
    key: `myHierarchy-${userEmail}`,
    url: "licenses/my-hierarchy",
    data: {
      aId: Cookies.get("aId"),
      userEmail,
    },
    options: {
      enabled: enabled && !!userEmail,
      select: (data: ApiResponseData) => isData(data),
    },
    post: true,
  });

// Dante Development Partner: Link pools to a CM account
export const useLinkContractManager = () =>
  usePostData({ url: "licenses/link-contract-manufacturer" });

// Dante Development Partner: Unlink pool from a CM account
export const useUnlinkContractManager = () =>
  useDeleteData({
    url: "licenses/unlink-contract-manufacturer"
  });
